import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { TrashIcon, type TrashIconHandle } from "@/components/ui/trash";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import { CloudUpload, Upload, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia } from "@/components/ui/empty";
import { FieldDescription, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  TRADE_DETAILS_JOURNAL_NOTES_CLASS,
  TRADE_DETAILS_UNSET_MOOD_VALUE,
  TradeDetailsJournalSelect,
} from "@/components/trade-filter-selects";

import { Trade } from "../../../../types/trade";
import { Attachment } from "../../../../types/attachment";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import {
  formatTradeDateTimeDetailForDisplay,
  formatTradeDateTimeForDisplay,
} from "../../../../utils/displayDateTime";
import {
  getAttachments,
  uploadAttachment,
  deleteAttachment,
  restoreAttachments,
  getAttachmentDataURL,
} from "../../../../services/attachmentService";
import { updateTrade } from "../../../../services/tradeApi";
import { backendTradeToFrontendTrade } from "../../../../services/tradeStorage";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import {
  TradeLogStatusBadges,
  TradeLogSyncTypeBadge,
  TradeLogTypeBadge,
} from "./tradeLogTradesTableBadges";
import { TradeLogSortableCell } from "./tradeLogSortableCell";

const LABEL_CLASS =
  "font-ibm-plex text-xs font-medium uppercase text-gray-400 dark:text-sidebar-muted-foreground";
const VALUE_CLASS =
  "font-ibm-plex text-sm font-medium text-gray-400 dark:text-sidebar-muted-foreground";
const SECTION_TITLE_CLASS = "font-[family-name:var(--font-primary)] text-lg font-normal text-[#38BBD1]";
/** Shared header + content sizing for market screenshot and message in trade details journal row. */
const TRADE_DETAILS_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";
const TRADE_DETAILS_FIELD_DESC_CLASS =
  "break-words font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground";
const TRADE_DETAILS_JOURNAL_INPUT_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 placeholder:text-gray-400 dark:text-sidebar-muted-foreground dark:placeholder:text-sidebar-muted-foreground/70";
const TRADE_DETAILS_FIELD_HEADER_CLASS = "mb-2 flex flex-col gap-1";
const TRADE_DETAILS_SECTION_GAP_CLASS = "gap-8";
const SCREENSHOT_BOX_CLASS =
  "h-[200px] w-full shrink-0 overflow-hidden rounded-[6px] border border-border bg-muted/20 dark:border-border dark:bg-sidebar";
const JOURNAL_NOTES_BOX_CLASS =
  "h-[200px] min-h-[200px] w-full shrink-0 border border-border bg-transparent dark:border-border dark:!bg-sidebar";
const UNSET_MOOD_VALUE = TRADE_DETAILS_UNSET_MOOD_VALUE;

/** Temporarily hide pre/during/post-trade mood selects in the trade details dialog. */
export const SHOW_TRADE_DETAILS_MOOD_SELECTS = false;

type TradeDetailsJournalSnapshot = {
  notes: string;
  preTradeMood: string;
  duringTradeMood: string;
  postTradeMood: string;
  attachments: Attachment[];
};

export type TradeDetailsJournalActions = {
  save: () => Promise<boolean>;
  discard: () => void;
};

function buildJournalSnapshot(trade: Trade, tradeAttachments: Attachment[]): TradeDetailsJournalSnapshot {
  return {
    notes: trade.notes || "",
    preTradeMood: trade.preTradeEmotion || "",
    duringTradeMood: trade.duringTradeEmotion || "",
    postTradeMood: trade.postTradeEmotion || "",
    attachments: tradeAttachments.map((attachment) => ({ ...attachment })),
  };
}

function journalSnapshotsEqual(a: TradeDetailsJournalSnapshot, b: TradeDetailsJournalSnapshot): boolean {
  if (
    a.notes !== b.notes ||
    a.preTradeMood !== b.preTradeMood ||
    a.duringTradeMood !== b.duringTradeMood ||
    a.postTradeMood !== b.postTradeMood
  ) {
    return false;
  }

  const aIds = a.attachments.map((attachment) => attachment.id).sort();
  const bIds = b.attachments.map((attachment) => attachment.id).sort();
  return aIds.length === bIds.length && aIds.every((id, index) => id === bIds[index]);
}

function handleMoodSelectChange(setMood: (value: string) => void) {
  return (value: string) => setMood(value === UNSET_MOOD_VALUE ? "" : value);
}

interface TradeDetailsCardProps {
  trade: Trade;
  hideHeader?: boolean;
  /**
   * Modal compact layout: one-row trade stats table + journal (screenshot / notes).
   * Never used for the Trade Log expanded row.
   */
  compactMode?: boolean;
  /**
   * Expanded Trade Log row: market screenshot + trade notes only.
   * No trade-stats table (that lives on the main row). Chart mounts in
   * TradeDetailsInlinePanel beside this card.
   */
  journalOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onRegisterJournalActions?: (actions: TradeDetailsJournalActions | null) => void;
  onJournalSaved?: (trade: Trade) => void;
}

const TradeDetailsCard: React.FC<TradeDetailsCardProps> = ({
  trade,
  hideHeader = false,
  compactMode = false,
  journalOnly = false,
  onDirtyChange,
  onRegisterJournalActions,
  onJournalSaved,
}) => {
  const { formatMoney } = useDisplayCurrency();
  const displayTimeZone = useDisplayTimeZone();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const screenshotTrashIconRef = useRef<TrashIconHandle | null>(null);

  const startScreenshotTrashIconAnimation = useCallback(() => {
    void screenshotTrashIconRef.current?.startAnimation();
  }, []);

  const stopScreenshotTrashIconAnimation = useCallback(() => {
    void screenshotTrashIconRef.current?.stopAnimation();
  }, []);

  const screenshotTrashIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startScreenshotTrashIconAnimation,
      onMouseEnter: startScreenshotTrashIconAnimation,
      onMouseLeave: stopScreenshotTrashIconAnimation,
    }),
    [startScreenshotTrashIconAnimation, stopScreenshotTrashIconAnimation]
  );

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [journalContent, setJournalContent] = useState(trade.notes || "");
  const [preTradeMood, setPreTradeMood] = useState<string>(trade.preTradeEmotion || "");
  const [duringTradeMood, setDuringTradeMood] = useState<string>(trade.duringTradeEmotion || "");
  const [postTradeMood, setPostTradeMood] = useState<string>(trade.postTradeEmotion || "");
  const [savedSnapshot, setSavedSnapshot] = useState<TradeDetailsJournalSnapshot | null>(null);
  const isJournalDirtyRef = useRef(false);

  const preTradeMoods = [
    { value: "calm", label: "Calm" },
    { value: "confident", label: "Confident" },
    { value: "excited", label: "Excited" },
    { value: "anxious", label: "Anxious" },
    { value: "uncertain", label: "Uncertain" },
    { value: "fearful", label: "Fearful" },
  ];

  const duringTradeMoods = [
    { value: "focused", label: "Focused" },
    { value: "calm", label: "Calm" },
    { value: "confident", label: "Confident" },
    { value: "nervous", label: "Nervous" },
    { value: "stressed", label: "Stressed" },
    { value: "impatient", label: "Impatient" },
    { value: "worried", label: "Worried" },
    { value: "relaxed", label: "Relaxed" },
  ];

  const postTradeMoods = [
    { value: "satisfied", label: "Satisfied" },
    { value: "proud", label: "Proud" },
    { value: "relieved", label: "Relieved" },
    { value: "frustrated", label: "Frustrated" },
    { value: "disappointed", label: "Disappointed" },
    { value: "regretful", label: "Regretful" },
  ];

  useEffect(() => {
    if (!trade.id || isJournalDirtyRef.current) return;
    let cancelled = false;
    void (async () => {
      try {
        const tradeAttachments = await getAttachments(trade.id);
        if (cancelled) return;
        const snapshot = buildJournalSnapshot(trade, tradeAttachments);
        setAttachments(tradeAttachments);
        setJournalContent(snapshot.notes);
        setPreTradeMood(snapshot.preTradeMood);
        setDuringTradeMood(snapshot.duringTradeMood);
        setPostTradeMood(snapshot.postTradeMood);
        setSavedSnapshot(snapshot);
      } catch (error: unknown) {
        if (cancelled) return;
        console.warn("Failed to load trade attachments:", error);
        const snapshot = buildJournalSnapshot(trade, []);
        setAttachments([]);
        setJournalContent(snapshot.notes);
        setPreTradeMood(snapshot.preTradeMood);
        setDuringTradeMood(snapshot.duringTradeMood);
        setPostTradeMood(snapshot.postTradeMood);
        setSavedSnapshot(snapshot);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    trade.id,
    trade.notes,
    trade.preTradeEmotion,
    trade.duringTradeEmotion,
    trade.postTradeEmotion,
    trade.updatedAt,
  ]);

  const currentSnapshot = useMemo<TradeDetailsJournalSnapshot>(
    () => ({
      notes: journalContent,
      preTradeMood,
      duringTradeMood,
      postTradeMood,
      attachments: attachments.map((attachment) => ({ ...attachment })),
    }),
    [attachments, duringTradeMood, journalContent, postTradeMood, preTradeMood]
  );

  const isJournalDirty =
    savedSnapshot !== null && !journalSnapshotsEqual(savedSnapshot, currentSnapshot);

  useEffect(() => {
    isJournalDirtyRef.current = isJournalDirty;
  }, [isJournalDirty]);

  useEffect(() => {
    onDirtyChange?.(isJournalDirty);
  }, [isJournalDirty, onDirtyChange]);

  const discardJournalChanges = useCallback(() => {
    if (!savedSnapshot || !trade.id) return;
    setJournalContent(savedSnapshot.notes);
    setPreTradeMood(savedSnapshot.preTradeMood);
    setDuringTradeMood(savedSnapshot.duringTradeMood);
    setPostTradeMood(savedSnapshot.postTradeMood);
    void restoreAttachments(trade.id, savedSnapshot.attachments)
      .then(() => {
        setAttachments(savedSnapshot.attachments.map((attachment) => ({ ...attachment })));
      })
      .catch((error: unknown) => {
        presentMutationError(error, "Error discarding changes");
      });
  }, [savedSnapshot, trade.id]);

  const handleSaveJournal = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      toastError("Authentication required", { description: "Please sign in to save journal data." });
      return false;
    }
    try {
      const updatedBackendTrade = await updateTrade(trade.id, {
        notes: journalContent.trim() ? journalContent : null,
        pre_trade_emotion: preTradeMood || null,
        during_trade_emotion: duringTradeMood || null,
        post_trade_emotion: postTradeMood || null,
      });
      const savedTrade = backendTradeToFrontendTrade(updatedBackendTrade);
      const tradeAttachments = await getAttachments(trade.id);
      const snapshot = buildJournalSnapshot(savedTrade, tradeAttachments);
      setJournalContent(snapshot.notes);
      setPreTradeMood(snapshot.preTradeMood);
      setDuringTradeMood(snapshot.duringTradeMood);
      setPostTradeMood(snapshot.postTradeMood);
      setSavedSnapshot(snapshot);
      onJournalSaved?.(savedTrade);
      toast.success("Changes saved", { description: "Your trade journal has been saved successfully." });
      return true;
    } catch (error: any) {
      presentMutationError(error, "Error saving journal");
      return false;
    }
  }, [
    duringTradeMood,
    journalContent,
    onJournalSaved,
    postTradeMood,
    preTradeMood,
    trade.id,
    user?.id,
  ]);

  useEffect(() => {
    if (!(compactMode || journalOnly) || !onRegisterJournalActions) return;
    onRegisterJournalActions({
      save: handleSaveJournal,
      discard: discardJournalChanges,
    });
    return () => onRegisterJournalActions(null);
  }, [compactMode, journalOnly, discardJournalChanges, handleSaveJournal, onRegisterJournalActions]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toastError("Invalid file type", { description: "Please select an image file (PNG, JPG, etc.)." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toastError("File too large", { description: "Please select an image smaller than 5MB." });
      return;
    }
    if (!user?.id) {
      toastError("Authentication required", { description: "Please sign in to upload attachments." });
      return;
    }
    setIsUploading(true);
    try {
      if (attachments.length > 0 && trade.id) {
        await Promise.all(attachments.map((att) => deleteAttachment(trade.id, att.id)));
      }
      const result = await uploadAttachment(file, trade.id, user.id);
      if (result.success && result.attachment) {
        setAttachments([result.attachment]);
        toast.success("Attachment uploaded", { description: `${file.name} has been uploaded successfully.` });
      } else {
        toastError("Upload failed", { description: result.error || "Failed to upload attachment." });
      }
    } catch (error) {
      toastError("Upload failed", { description: "An error occurred while uploading the attachment." });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleDeleteAttachment = (attachmentId: string) => {
    if (!trade.id) return;
    void deleteAttachment(trade.id, attachmentId)
      .then(() => {
        setAttachments([]);
        toast.success("Attachment removed", { description: "The screenshot has been removed." });
      })
      .catch((error: unknown) => {
        presentMutationError(error, "Delete failed");
      });
  };

  const currentAttachment = attachments.length > 0 ? attachments[0] : null;
  const imageUrl = currentAttachment ? getAttachmentDataURL(currentAttachment) : null;

  const formatPercent = (value: number): string => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

  const calculateRiskReward = (t: Trade): { rr: number; stopLoss: number; takeProfit: number } => {
    const entryPrice = t.entryPrice;
    const isLong = t.type === "buy";
    const riskPercent = 0.015;
    const stopLoss = isLong ? entryPrice * (1 - riskPercent) : entryPrice * (1 + riskPercent);
    if (t.status === "closed" && t.exitPrice) {
      const risk = isLong ? entryPrice - stopLoss : stopLoss - entryPrice;
      const reward = isLong ? t.exitPrice - entryPrice : entryPrice - t.exitPrice;
      if (risk > 0) {
        const rr = Math.abs(reward / risk);
        const takeProfit = isLong ? entryPrice + (risk * rr) : entryPrice - (risk * rr);
        return { rr, stopLoss, takeProfit };
      }
    }
    const typicalRR = 2;
    const risk = isLong ? entryPrice - stopLoss : stopLoss - entryPrice;
    const takeProfit = isLong ? entryPrice + (risk * typicalRR) : entryPrice - (risk * typicalRR);
    return { rr: typicalRR, stopLoss, takeProfit };
  };

  const { rr } = calculateRiskReward(trade);
  const formatDateTime = (date: Date | string): string => formatTradeDateTimeDetailForDisplay(date, displayTimeZone);
  const formatTradeTableDate = (date: Date | string): string =>
    formatTradeDateTimeForDisplay(date, displayTimeZone);
  const pnlColor = (value: number) => value >= 0 ? "text-[#06E363]" : "text-[#E00101]";
  const totalFees = (trade.entryFees || 0) + (trade.exitFees || 0);
  // Expanded-row path is journalOnly — never show the stats table there.
  const showStatsTable = compactMode && !journalOnly;
  const showJournalPanel = compactMode || journalOnly;

  return (
    <div
      data-trade-details-card=""
      className={cn(
        "min-w-0 max-w-full",
        journalOnly ? "overflow-visible" : "overflow-hidden",
        hideHeader ? "p-0" : "rounded-[6px] bg-white p-6 shadow-sm dark:bg-[#151517]",
      )}
    >
      {/* Header */}
      {!hideHeader && (
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className={SECTION_TITLE_CLASS}>Trade Details</h2>
        </div>
      )}

      <div className="flex w-full min-w-0 flex-col gap-6">
        {showJournalPanel ? (
          <>
            <div
              className={cn(
                "flex w-full flex-col",
                journalOnly ? "gap-6" : TRADE_DETAILS_SECTION_GAP_CLASS
              )}
            >
              {showStatsTable ? (
              <div className="w-full">
                <div
                  data-trade-details-summary-table=""
                  data-trade-log-trades-table=""
                  className="w-full"
                >
                  <div
                    data-shadcn-data-table
                    className="overflow-hidden rounded-[6px] border border-border bg-card shadow-none dark:bg-sidebar"
                  >
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="min-w-[96px]">Symbol</TableHead>
                        <TableHead className="min-w-[72px]">Type</TableHead>
                        <TableHead className="min-w-[120px]">Status</TableHead>
                        <TableHead className="min-w-[104px]">Sync Type</TableHead>
                        <TableHead className="min-w-[160px]">Entry Date</TableHead>
                        <TableHead className="min-w-[160px]">Exit Date</TableHead>
                        <TableHead className="min-w-[112px]" data-column-align="end">Entry Price</TableHead>
                        <TableHead className="min-w-[112px]" data-column-align="end">Exit Price</TableHead>
                        <TableHead className="min-w-[88px]" data-column-align="end">Lot Size</TableHead>
                        <TableHead className="min-w-[96px]" data-column-align="end">Fees</TableHead>
                        <TableHead className="min-w-[104px]" data-column-align="end">Gross P&L</TableHead>
                        <TableHead className="min-w-[104px]" data-column-align="end">Net P&L</TableHead>
                        <TableHead className="min-w-[96px]" data-column-align="end">Return %</TableHead>
                        <TableHead className="min-w-[88px]" data-column-align="end">ROI</TableHead>
                        <TableHead className="min-w-[88px]" data-column-align="end">R/R</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="hover:bg-muted/50 dark:hover:bg-transparent">
                        <TableCell>
                          <TradeLogSortableCell className="font-medium">{trade.symbol}</TradeLogSortableCell>
                        </TableCell>
                        <TableCell>
                          <div className="flex min-h-9 items-center">
                            <TradeLogTypeBadge type={trade.type} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex min-h-9 flex-wrap items-center gap-1">
                            <TradeLogStatusBadges trade={trade} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex min-h-9 items-center">
                            <TradeLogSyncTypeBadge trade={trade} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <TradeLogSortableCell>{formatTradeTableDate(trade.entryDate)}</TradeLogSortableCell>
                        </TableCell>
                        <TableCell>
                          <TradeLogSortableCell>
                            {trade.exitDate ? formatTradeTableDate(trade.exitDate) : "—"}
                          </TradeLogSortableCell>
                        </TableCell>
                        <TableCell data-column-align="end">
                          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                            {formatMoney(trade.entryPrice)}
                          </TradeLogSortableCell>
                        </TableCell>
                        <TableCell data-column-align="end">
                          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                            {trade.exitPrice != null ? formatMoney(trade.exitPrice) : "—"}
                          </TradeLogSortableCell>
                        </TableCell>
                        <TableCell data-column-align="end">
                          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                            {trade.entryQuantity}
                          </TradeLogSortableCell>
                        </TableCell>
                        <TableCell data-column-align="end">
                          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                            {formatMoney(totalFees)}
                          </TradeLogSortableCell>
                        </TableCell>
                        <TableCell data-column-align="end">
                          {typeof trade.grossPnL === "number" && Number.isFinite(trade.grossPnL) ? (
                            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                              <span data-trade-log-pnl="" style={{ color: pnlBarFill(trade.grossPnL) }}>
                                {formatMoney(trade.grossPnL)}
                              </span>
                            </TradeLogSortableCell>
                          ) : (
                            <TradeLogSortableCell align="right" className="tabular-nums">—</TradeLogSortableCell>
                          )}
                        </TableCell>
                        <TableCell data-column-align="end">
                          {typeof trade.netPnL === "number" && Number.isFinite(trade.netPnL) ? (
                            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                              <span data-trade-log-pnl="" style={{ color: pnlBarFill(trade.netPnL) }}>
                                {formatMoney(trade.netPnL)}
                              </span>
                            </TradeLogSortableCell>
                          ) : (
                            <TradeLogSortableCell align="right" className="tabular-nums">—</TradeLogSortableCell>
                          )}
                        </TableCell>
                        <TableCell data-column-align="end">
                          {typeof trade.returnPercent === "number" && Number.isFinite(trade.returnPercent) ? (
                            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                              {formatPercent(trade.returnPercent)}
                            </TradeLogSortableCell>
                          ) : (
                            <TradeLogSortableCell align="right" className="tabular-nums">—</TradeLogSortableCell>
                          )}
                        </TableCell>
                        <TableCell data-column-align="end">
                          {typeof trade.roi === "number" && Number.isFinite(trade.roi) ? (
                            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                              {formatPercent(trade.roi)}
                            </TradeLogSortableCell>
                          ) : (
                            <TradeLogSortableCell align="right" className="tabular-nums">—</TradeLogSortableCell>
                          )}
                        </TableCell>
                        <TableCell data-column-align="end">
                          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
                            {`${rr.toFixed(2)}:1`}
                          </TradeLogSortableCell>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  </div>
                </div>
              </div>
              ) : null}

              {SHOW_TRADE_DETAILS_MOOD_SELECTS ? (
                <div
                  className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6"
                  data-trade-details-journal-moods=""
                  data-trade-log-filter-bar=""
                >
                  <div className="flex min-w-0 flex-col">
                    <div className={TRADE_DETAILS_FIELD_HEADER_CLASS}>
                      <FieldLabel htmlFor="pre-trade-mood" className={TRADE_DETAILS_FIELD_LABEL_CLASS}>
                        Pre-Trade Mood
                      </FieldLabel>
                      <FieldDescription className={TRADE_DETAILS_FIELD_DESC_CLASS}>
                        How did you feel before entering?
                      </FieldDescription>
                    </div>
                    <TradeDetailsJournalSelect
                      id="pre-trade-mood"
                      value={preTradeMood}
                      onValueChange={handleMoodSelectChange(setPreTradeMood)}
                      options={preTradeMoods}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <div className={TRADE_DETAILS_FIELD_HEADER_CLASS}>
                      <FieldLabel htmlFor="during-trade-mood" className={TRADE_DETAILS_FIELD_LABEL_CLASS}>
                        During Trade Mood
                      </FieldLabel>
                      <FieldDescription className={TRADE_DETAILS_FIELD_DESC_CLASS}>
                        How did you feel while in the trade?
                      </FieldDescription>
                    </div>
                    <TradeDetailsJournalSelect
                      id="during-trade-mood"
                      value={duringTradeMood}
                      onValueChange={handleMoodSelectChange(setDuringTradeMood)}
                      options={duringTradeMoods}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <div className={TRADE_DETAILS_FIELD_HEADER_CLASS}>
                      <FieldLabel htmlFor="post-trade-mood" className={TRADE_DETAILS_FIELD_LABEL_CLASS}>
                        Post-Trade Mood
                      </FieldLabel>
                      <FieldDescription className={TRADE_DETAILS_FIELD_DESC_CLASS}>
                        How did you feel after closing?
                      </FieldDescription>
                    </div>
                    <TradeDetailsJournalSelect
                      id="post-trade-mood"
                      value={postTradeMood}
                      onValueChange={handleMoodSelectChange(setPostTradeMood)}
                      options={postTradeMoods}
                    />
                  </div>
                </div>
              ) : null}

              <div
                className="grid w-full min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-6"
                data-trade-details-journal-row=""
              >
                <div className="flex min-w-0 flex-col">
                  <div className={TRADE_DETAILS_FIELD_HEADER_CLASS} data-trade-details-journal-header="">
                    <FieldLabel className={TRADE_DETAILS_FIELD_LABEL_CLASS}>Market Screenshot</FieldLabel>
                    <FieldDescription className={TRADE_DETAILS_FIELD_DESC_CLASS}>
                      Upload a market screenshot for this trade. Saved to your journal and synced across devices.
                    </FieldDescription>
                  </div>
                  <div
                    className={cn(
                      "relative flex items-center justify-center",
                      SCREENSHOT_BOX_CLASS,
                      imageUrl && "p-0"
                    )}
                    data-trade-details-screenshot-box=""
                    data-trade-details-journal-control=""
                  >
                    {imageUrl ? (
                      <>
                        <img
                          src={imageUrl}
                          alt={currentAttachment?.fileName || "Market screenshot"}
                          className="h-full min-h-0 w-full cursor-pointer object-contain"
                          onClick={() => setIsImageModalOpen(true)}
                        />
                        <button
                          type="button"
                          className="absolute right-2 top-2 z-[2] flex size-8 items-center justify-center rounded-full bg-[#E00101]/80 text-white shadow-sm transition-colors hover:bg-[#E00101] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E00101]/40 [&_svg]:text-current"
                          onClick={(e) => { e.stopPropagation(); if (currentAttachment) handleDeleteAttachment(currentAttachment.id); }}
                          aria-label="Remove screenshot"
                          {...screenshotTrashIconHoverHandlers}
                        >
                          <TrashIcon
                            ref={screenshotTrashIconRef}
                            size={16}
                            className="shrink-0 text-current"
                            aria-hidden
                          />
                        </button>
                      </>
                    ) : (
                      <Empty
                        variant="outline"
                        data-trade-details-screenshot-empty=""
                        className="h-full min-h-0 w-full !gap-3 !rounded-[6px] !border-2 !border-dashed !border-gray-300 !bg-transparent !p-4 dark:!border-border"
                      >
                        <EmptyHeader className="gap-2">
                          <EmptyMedia variant="icon">
                            <CloudUpload className="size-4 text-muted-foreground dark:text-sidebar-muted-foreground" aria-hidden />
                          </EmptyMedia>
                          <EmptyDescription className="font-[family-name:var(--font-primary)] text-sm font-normal leading-relaxed text-gray-500 dark:text-sidebar-muted-foreground">
                            Upload a screenshot for this trade to keep visual context.
                          </EmptyDescription>
                        </EmptyHeader>
                        <EmptyContent className="gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            data-font="primary"
                            data-trade-log-action=""
                            className="gap-1.5 !border-gray-300 !text-gray-500 dark:!border-border dark:!text-sidebar-muted-foreground"
                            onClick={handleUploadClick}
                            disabled={isUploading}
                          >
                            {isUploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                            <span>{isUploading ? "Uploading..." : "Upload Files"}</span>
                          </Button>
                          <EmptyDescription className="font-[family-name:var(--font-primary)] text-xs text-gray-400 dark:text-sidebar-muted-foreground">
                            PNG, JPG up to 5MB
                          </EmptyDescription>
                        </EmptyContent>
                      </Empty>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
                  </div>
                </div>

                <div className="flex min-w-0 flex-col" data-trade-details-notes-field="">
                  <div className={TRADE_DETAILS_FIELD_HEADER_CLASS} data-trade-details-journal-header="">
                    <FieldLabel htmlFor="trade-notes" className={TRADE_DETAILS_FIELD_LABEL_CLASS}>
                      Trade Notes
                    </FieldLabel>
                    <FieldDescription className={TRADE_DETAILS_FIELD_DESC_CLASS}>
                      Add notes for this trade.
                    </FieldDescription>
                  </div>
                  <Textarea
                    id="trade-notes"
                    data-trade-details-notes=""
                    data-trade-details-journal-control=""
                    value={journalContent}
                    onChange={(event) => setJournalContent(event.target.value)}
                    placeholder="Add notes for this trade..."
                    className={cn(
                      JOURNAL_NOTES_BOX_CLASS,
                      "resize-none overflow-y-auto rounded-[6px]",
                      TRADE_DETAILS_JOURNAL_INPUT_CLASS,
                      TRADE_DETAILS_JOURNAL_NOTES_CLASS
                    )}
                  />
                </div>
              </div>

              {/* Chart lives only in TradeDetailsInlinePanel (expanded row), not modal / full mode. */}
            </div>
          </>
        ) : (
          <>
            {/* Full Mode - 3 column grid */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Trade Overview */}
              <div>
                <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Trade Overview</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Symbol</p>
                    <span className={VALUE_CLASS}>{trade.symbol}</span>
                  </div>
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Type</p>
                    <div className="mt-0.5 flex items-center">
                      <TradeLogTypeBadge type={trade.type} />
                    </div>
                  </div>
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Status</p>
                    <div className="mt-0.5 flex items-center">
                      <TradeLogStatusBadges trade={trade} />
                    </div>
                  </div>
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Sync Type</p>
                    <div className="mt-0.5 flex items-center">
                      <TradeLogSyncTypeBadge trade={trade} />
                    </div>
                  </div>
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Entry Date/Time</p>
                    <p className={VALUE_CLASS}>{formatDateTime(trade.entryDate)}</p>
                  </div>
                  {trade.exitDate && (
                    <div>
                      <p className={cn(LABEL_CLASS, "mb-1")}>Exit Date/Time</p>
                      <p className={VALUE_CLASS}>{formatDateTime(trade.exitDate)}</p>
                    </div>
                  )}
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Risk/Reward</p>
                    <p className={VALUE_CLASS}>{rr.toFixed(2)}:1</p>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div>
                <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Financial Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className={cn(LABEL_CLASS, "mb-1")}>Entry Price</p><p className={VALUE_CLASS}>{formatMoney(trade.entryPrice)}</p></div>
                  <div><p className={cn(LABEL_CLASS, "mb-1")}>Lot Size</p><p className={VALUE_CLASS}>{trade.entryQuantity}</p></div>
                  {trade.exitPrice && <div><p className={cn(LABEL_CLASS, "mb-1")}>Exit Price</p><p className={VALUE_CLASS}>{formatMoney(trade.exitPrice)}</p></div>}
                  <div><p className={cn(LABEL_CLASS, "mb-1")}>Total Fees</p><p className={VALUE_CLASS}>{formatMoney((trade.entryFees || 0) + (trade.exitFees || 0))}</p></div>
                  {trade.grossPnL !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Gross P&L</p><p className={cn("font-ibm-plex text-sm font-medium", pnlColor(trade.grossPnL))}>{formatMoney(trade.grossPnL)}</p></div>}
                  {trade.netPnL !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Net P&L</p><p className={cn("font-ibm-plex text-sm font-medium", pnlColor(trade.netPnL))}>{formatMoney(trade.netPnL)}</p></div>}
                  {trade.returnPercent !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Return %</p><p className={cn("font-ibm-plex text-sm font-medium", pnlColor(trade.returnPercent))}>{formatPercent(trade.returnPercent)}</p></div>}
                  {trade.roi !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>ROI</p><p className={cn("font-ibm-plex text-sm font-medium", pnlColor(trade.roi))}>{formatPercent(trade.roi)}</p></div>}
                </div>
              </div>

              {/* Additional Information */}
              {trade.notes ? (
                <div>
                  <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Additional Information</h3>
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Notes</p>
                    <div className="prose prose-sm max-w-none font-ibm-plex text-sm font-medium text-gray-400 dark:text-sidebar-muted-foreground [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:mt-4 [&_h3]:mb-2 [&_ol]:ml-6 [&_ol]:mb-2 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:ml-6 [&_ul]:mb-2" dangerouslySetInnerHTML={{ __html: trade.notes }} />
                  </div>
                </div>
              ) : <div />}
            </div>

            {/* Pre-Trade Plan */}
            {(trade.preTradeRationale || trade.preTradeTargets || trade.preTradeRiskPlan || trade.preTradeMarketConditions) && (
              <>
                <hr className="border-border" />
                <div>
                  <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Pre-Trade Plan</h3>
                  <div className="flex flex-col gap-4">
                    {trade.preTradeRationale && <div><p className={cn(LABEL_CLASS, "mb-1")}>Entry Rationale</p><p className={cn(VALUE_CLASS, "whitespace-pre-wrap")}>{trade.preTradeRationale}</p></div>}
                    {trade.preTradeTargets && <div><p className={cn(LABEL_CLASS, "mb-1")}>Expected Price Targets</p><p className={cn(VALUE_CLASS, "whitespace-pre-wrap")}>{trade.preTradeTargets}</p></div>}
                    {trade.preTradeRiskPlan && <div><p className={cn(LABEL_CLASS, "mb-1")}>Risk Management Plan</p><p className={cn(VALUE_CLASS, "whitespace-pre-wrap")}>{trade.preTradeRiskPlan}</p></div>}
                    {trade.preTradeMarketConditions && <div><p className={cn(LABEL_CLASS, "mb-1")}>Expected Market Conditions</p><p className={cn(VALUE_CLASS, "whitespace-pre-wrap")}>{trade.preTradeMarketConditions}</p></div>}
                  </div>
                </div>
              </>
            )}

            {/* Trade Plan vs. Execution */}
            {(trade.plannedEntryPrice || trade.plannedExitPrice || trade.plannedStopLoss || trade.plannedTakeProfit || trade.plannedRisk || trade.plannedDuration || trade.mfe || trade.mae) && (
              <>
                <hr className="border-border" />
                <div>
                  <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Trade Plan vs. Execution</h3>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {trade.plannedEntryPrice !== undefined && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Planned Entry</p>
                        <p className={VALUE_CLASS}>{formatMoney(trade.plannedEntryPrice)}</p>
                        {trade.entryPrice && <p className={cn("mt-1 font-ibm-plex text-xs", trade.entryPrice === trade.plannedEntryPrice ? "text-[#06E363]" : "text-orange-500")}>Actual: {formatMoney(trade.entryPrice)}</p>}
                      </div>
                    )}
                    {trade.plannedExitPrice !== undefined && trade.status === "closed" && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Planned Exit</p>
                        <p className={VALUE_CLASS}>{formatMoney(trade.plannedExitPrice)}</p>
                        {trade.exitPrice && <p className={cn("mt-1 font-ibm-plex text-xs", Math.abs((trade.exitPrice || 0) - trade.plannedExitPrice) < 0.01 ? "text-[#06E363]" : "text-orange-500")}>Actual: {formatMoney(trade.exitPrice)}</p>}
                      </div>
                    )}
                    {trade.plannedStopLoss !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Planned Stop Loss</p><p className={VALUE_CLASS}>{formatMoney(trade.plannedStopLoss)}</p></div>}
                    {trade.plannedTakeProfit !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Planned Take Profit</p><p className={VALUE_CLASS}>{formatMoney(trade.plannedTakeProfit)}</p></div>}
                    {trade.plannedRisk !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Planned Risk</p><p className={VALUE_CLASS}>{formatMoney(trade.plannedRisk)}</p></div>}
                    {trade.plannedDuration !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Planned Duration</p><p className={VALUE_CLASS}>{trade.plannedDuration} hours</p></div>}
                    {trade.mfe !== undefined && trade.status === "closed" && <div><p className={cn(LABEL_CLASS, "mb-1")}>MFE (Max Favorable)</p><p className="font-ibm-plex text-sm font-medium text-[#06E363]">{formatMoney(trade.mfe)}</p></div>}
                    {trade.mae !== undefined && trade.status === "closed" && <div><p className={cn(LABEL_CLASS, "mb-1")}>MAE (Max Adverse)</p><p className="font-ibm-plex text-sm font-medium text-[#E00101]">{formatMoney(trade.mae)}</p></div>}
                  </div>
                </div>
              </>
            )}

            {/* Psychological Tracking */}
            {(trade.preTradeConfidence !== undefined || trade.preTradeEmotion || trade.followedPlan !== undefined || trade.temptedEarlyExit !== undefined || trade.addedToPosition !== undefined || trade.postTradeEmotion || trade.emotionsInvolved !== undefined) && (
              <>
                <hr className="border-border" />
                <div>
                  <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Psychological Tracking</h3>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                    {trade.preTradeConfidence !== undefined && <div><p className={cn(LABEL_CLASS, "mb-1")}>Pre-Trade Confidence</p><p className={VALUE_CLASS}>{trade.preTradeConfidence}/10</p></div>}
                    {trade.preTradeEmotion && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Pre-Trade Emotion</p>
                        <span className="rounded-full bg-gradient-to-br from-[#38BBD1] to-[#3EEAEC] px-3 py-1 font-ibm-plex text-xs font-medium capitalize text-white">{trade.preTradeEmotion}</span>
                      </div>
                    )}
                    {trade.followedPlan !== undefined && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Followed Plan</p>
                        <span className={cn("rounded-full px-3 py-1 font-ibm-plex text-xs font-medium text-white", trade.followedPlan ? "bg-gradient-to-br from-[#06E363] to-[#AAFAB1]" : "bg-gradient-to-br from-[#E00101] to-[#FE4244]")}>{trade.followedPlan ? "Yes" : "No"}</span>
                      </div>
                    )}
                    {trade.temptedEarlyExit !== undefined && trade.temptedEarlyExit && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Tempted Early Exit</p>
                        <span className="rounded-full bg-gradient-to-br from-[#F45B0B] to-[#F49113] px-3 py-1 font-ibm-plex text-xs font-medium text-white">Yes</span>
                      </div>
                    )}
                    {trade.addedToPosition !== undefined && trade.addedToPosition && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Added to Position</p>
                        <span className="rounded-full bg-gradient-to-br from-[#F45B0B] to-[#F49113] px-3 py-1 font-ibm-plex text-xs font-medium text-white">Yes (FOMO)</span>
                      </div>
                    )}
                    {trade.postTradeEmotion && trade.status === "closed" && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Post-Trade Emotion</p>
                        <span className="rounded-full bg-gradient-to-br from-[#38BBD1] to-[#3EEAEC] px-3 py-1 font-ibm-plex text-xs font-medium capitalize text-white">{trade.postTradeEmotion}</span>
                      </div>
                    )}
                    {trade.emotionsInvolved !== undefined && trade.status === "closed" && (
                      <div>
                        <p className={cn(LABEL_CLASS, "mb-1")}>Emotions Involved</p>
                        <span className={cn("rounded-full px-3 py-1 font-ibm-plex text-xs font-medium text-white", trade.emotionsInvolved ? "bg-gradient-to-br from-[#E00101] to-[#FE4244]" : "bg-gradient-to-br from-[#06E363] to-[#AAFAB1]")}>{trade.emotionsInvolved ? "Yes" : "No"}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Market Context (shared between modes) */}
        {(trade.marketDirection || trade.sectorMovements || trade.volumeAnalysis || trade.marketNews || trade.sentimentIndicators) && (
          <>
            <hr className="border-border" />
            <div>
              <h3 className={cn(SECTION_TITLE_CLASS, "mb-4")}>Market Context</h3>
              <div className="flex flex-col gap-4">
                {trade.marketDirection && (
                  <div>
                    <p className={cn(LABEL_CLASS, "mb-1")}>Market Direction</p>
                    <span
                      className="rounded-full px-3 py-1 font-ibm-plex text-xs font-medium capitalize text-white"
                      style={{ backgroundImage: trade.marketDirection === "bullish" ? "linear-gradient(135deg, #06E363, #AAFAB1)" : trade.marketDirection === "bearish" ? "linear-gradient(135deg, #E00101, #FE4244)" : "linear-gradient(135deg, #38BBD1, #3EEAEC)" }}
                    >
                      {trade.marketDirection}
                    </span>
                  </div>
                )}
                {trade.sectorMovements && <div><p className={cn(LABEL_CLASS, "mb-1")}>Sector Movements</p><p className={VALUE_CLASS}>{trade.sectorMovements}</p></div>}
                {trade.volumeAnalysis && <div><p className={cn(LABEL_CLASS, "mb-1")}>Volume Analysis</p><p className={cn(VALUE_CLASS, "whitespace-pre-wrap")}>{trade.volumeAnalysis}</p></div>}
                {trade.marketNews && <div><p className={cn(LABEL_CLASS, "mb-1")}>News/Events</p><p className={cn(VALUE_CLASS, "whitespace-pre-wrap")}>{trade.marketNews}</p></div>}
                {trade.sentimentIndicators && <div><p className={cn(LABEL_CLASS, "mb-1")}>Sentiment Indicators</p><p className={VALUE_CLASS}>{trade.sentimentIndicators}</p></div>}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={isImageModalOpen && Boolean(imageUrl)}
        onOpenChange={setIsImageModalOpen}
      >
        <DialogContent
          data-trade-details-screenshot-preview=""
          className="flex w-[min(96vw,80rem)] max-w-[min(96vw,80rem)] flex-col gap-0 border-none bg-background p-3 sm:max-w-[min(96vw,80rem)]"
        >
          <DialogTitle className="sr-only">
            {currentAttachment?.fileName || "Market screenshot"}
          </DialogTitle>
          <div className="flex max-h-[min(85vh,48rem)] w-full items-center justify-center">
            <img
              src={imageUrl || ""}
              alt={currentAttachment?.fileName || "Market screenshot"}
              className="max-h-[min(85vh,48rem)] max-w-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TradeDetailsCard;
